<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';

import {
  useCoreStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type {
  AccommodationProductCardData,
  AlternativeHotel,
  AvailableItineraryAccommodationSegment,
} from '@apps/main/new-desktop/pages/tripOverview/models';

import {
  AppArrowScroller,
  AppButton,
  AppAutoplayGallery,
  AppLazyLoadComponent,
} from '@libs/ui';

import DateCardTag from '../DateCardTag.vue';

import DesktopProductCardLayout from './DesktopProductCardLayout.vue';
import AccommodationProductCardContent from './AccommodationProductCardContent.vue';
import AlternativeAccommodationCardContent from './AlternativeAccommodationCardContent.vue';
import AccommodationPrice from './AccommodationPrice.vue';
import AccommodationCardSelectedFooter from './AccommodationCardSelectedFooter.vue';
import GenericHotelCard from './GenericHotelCard.vue';

interface AccommodationCarouselDesktopProps {
  segment: AvailableItineraryAccommodationSegment;
  printMode: boolean;
}

const props = defineProps<AccommodationCarouselDesktopProps>();

const coreStore = useCoreStore();
const statsigEventsStore = useStatsigEventsStore();
const routerStore = useRouterStore();

const activeAutoplayGalleryIndex = ref<number | null>(null);
const firstCardRef = useTemplateRef('firstCardRef');
const firstCardSize = useElementSize(firstCardRef);

const hotelCards = computed(() => {
  if (props.printMode) {
    return [props.segment.productCardData];
  }
  return [
    props.segment.productCardData,
    ...props.segment.alternativeHotels,
    null,
  ];
});

function showDetails(
  hotel: AlternativeHotel | AccommodationProductCardData,
  context: {
    isAlternative: boolean;
    shouldScrollToRooms: boolean;
    position?: number;
    clickSource:
      | 'detailsButtonExperiment'
      | 'mainCard'
      | 'suggestionCard'
      | 'changeAccommodationButtonOnSuggestionCard'
      | undefined;
  }
): void {
  if (props.printMode) {
    return;
  }

  const hotelCode = isSelectedHotel(hotel)
    ? props.segment.context.hotelCode
    : hotel.hotelCode;

  if (context.isAlternative && context.position) {
    statsigEventsStore.isAlternativeHotelOpened = true;
    statsigEventsStore.logTripOverviewAlternativeHotelsCardClicked({
      position: context.position,
      hotelCode: hotelCode,
    });
  }

  routerStore.navigateToHotelDetailsInTripOverview(
    {
      ingredientIndex: props.segment.context.ingredientIndex,
    },
    hotelCode,
    context.shouldScrollToRooms
  );
}

function isSelectedHotel(
  hotel: AlternativeHotel | AccommodationProductCardData
): hotel is AccommodationProductCardData {
  return 'productCardContent' in hotel;
}

function showDetailsExperiment(
  accommodationItem: AlternativeHotel | AccommodationProductCardData,
  clickSource: 'mainCard' | undefined
): void {
  showDetails(accommodationItem, {
    isAlternative: false,
    shouldScrollToRooms: false,
    clickSource,
  });
}
</script>

<template>
  <div
    :class="[
      'accommodation-products-carousel-desktop',
      { 'print-mode': props.printMode },
    ]"
  >
    <AppArrowScroller
      :items="hotelCards"
      :item-key-selector="(_, index) => index"
      :arrow-vertical-position="firstCardSize.height.value / 2"
      :display-dashed-border="false"
      :print-mode="props.printMode"
      :show-arrows="hotelCards.length > 1"
      :left-arrow-aria-label="$t('a11y.left_arrow_previous_suggestions')"
      :right-arrow-aria-label="$t('a11y.right_arrow_next_suggestions')"
    >
      <template #item="{ item: accommodationItem, index }">
        <div
          v-if="accommodationItem && isSelectedHotel(accommodationItem)"
          :class="['card']"
        >
          <DesktopProductCardLayout
            ref="firstCardRef"
            :print-mode="props.printMode"
            @mouseenter="activeAutoplayGalleryIndex = index"
            @mouseleave="activeAutoplayGalleryIndex = null"
            @focusin="activeAutoplayGalleryIndex = index"
            @focusout="activeAutoplayGalleryIndex = null"
          >
            <template #left-section>
              <AppLazyLoadComponent>
                <AppAutoplayGallery
                  :image-urls="accommodationItem.imgSrcs"
                  :is-active="
                    !props.printMode && activeAutoplayGalleryIndex === index
                  "
                  :error-image-handler="
                    (src) =>
                      coreStore.handleImageLoadError({
                        src: src,
                        source:
                          'accommodation-products-carousel-desktop-first-card-autoplay-gallery',
                      })
                  "
                  @click="showDetailsExperiment(accommodationItem, 'mainCard')"
                />
              </AppLazyLoadComponent>
            </template>
            <template #content>
              <AccommodationProductCardContent
                :product-card-content="accommodationItem.productCardContent"
                :context="props.segment.context"
                :print-mode="props.printMode"
                @show-details="
                  showDetailsExperiment(accommodationItem, 'mainCard')
                "
                @show-rooms="
                  showDetails(accommodationItem, {
                    isAlternative: false,
                    shouldScrollToRooms: true,
                    clickSource: undefined,
                  })
                "
              />
            </template>
            <template #footer>
              <AccommodationCardSelectedFooter
                v-if="!props.printMode"
                :ingredient-index="props.segment.context.ingredientIndex"
              />
            </template>
            <template #left-section-tag>
              <DateCardTag :date="props.segment.productCardData.date" />
            </template>
          </DesktopProductCardLayout>
        </div>
        <div
          v-if="
            accommodationItem &&
            !isSelectedHotel(accommodationItem) &&
            !props.printMode
          "
          class="card"
        >
          <DesktopProductCardLayout
            @mouseenter="activeAutoplayGalleryIndex = index"
            @mouseleave="activeAutoplayGalleryIndex = null"
            @focusin="activeAutoplayGalleryIndex = index"
            @focusout="activeAutoplayGalleryIndex = null"
          >
            <template #left-section>
              <AppLazyLoadComponent>
                <AppAutoplayGallery
                  :image-urls="accommodationItem.imgUrls"
                  :is-active="
                    !props.printMode && activeAutoplayGalleryIndex === index
                  "
                  :error-image-handler="
                    (src) =>
                      coreStore.handleImageLoadError({
                        src: src,
                        source:
                          'accommodation-products-carousel-desktop-autoplay-gallery',
                      })
                  "
                  @click="
                    showDetails(accommodationItem, {
                      isAlternative: true,
                      shouldScrollToRooms: false,
                      position: index - 1,
                      clickSource: 'suggestionCard',
                    })
                  "
                />
              </AppLazyLoadComponent>
            </template>
            <template #content>
              <AlternativeAccommodationCardContent
                :hotel="accommodationItem"
                :ingredient-index="props.segment.context.ingredientIndex"
                @show-details="
                  showDetails(accommodationItem, {
                    isAlternative: true,
                    shouldScrollToRooms: false,
                    position: index - 1,
                    clickSource: 'suggestionCard',
                  })
                "
                @show-rooms="
                  showDetails(accommodationItem, {
                    isAlternative: true,
                    shouldScrollToRooms: true,
                    position: index - 1,
                    clickSource: undefined,
                  })
                "
              />
            </template>
            <template #footer>
              <div class="footer">
                <AccommodationPrice :price="accommodationItem.deltaPrice" />
                <AppButton
                  type="primary"
                  :left-icon="{ name: 'change' }"
                  state="enabled"
                  size="small"
                  @click="
                    (showDetails(accommodationItem, {
                      isAlternative: true,
                      shouldScrollToRooms: false,
                      clickSource: 'changeAccommodationButtonOnSuggestionCard',
                    }),
                    statsigEventsStore.logTripOverviewChangeAlternateHotelCardClicked())
                  "
                >
                  {{ $t('overview_stay_footer.change') }}
                </AppButton>
              </div>
            </template>
            <template #left-section-tag>
              <DateCardTag :date="props.segment.productCardData.date" />
            </template>
          </DesktopProductCardLayout>
        </div>

        <div v-if="accommodationItem === null && !props.printMode" class="card">
          <GenericHotelCard
            :ingredient-index="props.segment.context.ingredientIndex"
          />
        </div>
      </template>
    </AppArrowScroller>
    <slot />
  </div>
</template>

<style scoped lang="scss">
.accommodation-products-carousel-desktop {
  --card-width: 54vw;
  --card-max-width: 850px;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  background-color: var(--color-off-white);

  @include for-less-or-equal-than-1199-px {
    --card-width: calc(90vw - 60px);
  }

  &.print-mode {
    background-color: var(--color-white);
    --card-width: 100%;

    .card {
      cursor: default;
    }
  }

  .card {
    display: grid;
    grid-auto-rows: 1fr;
    width: var(--card-width);
    height: 100%;
    max-width: var(--card-max-width);
    margin-right: var(--spacing-12);
    cursor: pointer;

    .footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin: var(--spacing-12) 0 0 0;
      flex-wrap: wrap;
    }

    &.single {
      margin-right: 0;
      --card-width: 100%;
      --card-max-width: 100%;

      .trash-icon {
        position: absolute;
        top: var(--spacing-16);
        right: var(--spacing-16);
      }

      .button-wrapper {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-16);
        align-items: center;

        .details {
          @include font-label-m-light;
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }

    &.generic {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: var(--spacing-24);
      background-color: var(--color-neutral-10);
      width: var(--card-width);
      max-width: var(--card-max-width);
      border-radius: var(--border-radius-8);
      margin-right: 0;
      overflow: hidden;
      height: 100%;

      @include on-hover {
        cursor: default;
      }

      .generic-card-title {
        color: var(--color-soft-black);
        text-align: center;
        @include font-label-m-bold;
      }

      .heart-illustration {
        position: absolute;
        top: 9px;
        left: 40px;
      }
      .star-illustration {
        position: absolute;
        bottom: -18px;
        right: 25px;
      }
    }
  }
}
</style>
